import React from 'react'
import { formatEventTime } from '../utils/formatters'
import LinkPreviewCard from './LinkPreviewCard'
import MediaRenderer from './MediaRenderer'
import CodeLogCard from './CodeLogCard'

/* ───────────────────────────────────────────────────────────────
 *  EVENT CARD CONFIG — tune the look here
 * ─────────────────────────────────────────────────────────────── */
const EVENT_CONFIG = {
  cardBackground: 'var(--color-bg-card-solid)',
  cardBorderRadius: '18px',
  cardPadding: '16px 18px',
  cardMarginTop: '12px',
  cardMaxWidth: '400px',
  cardGap: '12px',

  titleFontSize: '18px',
  titleFontWeight: '600',
  titleColor: 'var(--color-text-primary)',
  titleMarginBottom: '4px',
  titleMarginBottomNoDesc: '10px',

  descFontSize: '16px',
  descColor: 'var(--color-text-secondary)',
  descMarginBottom: '6px',

  timePillFontSize: '13px',
  timePillFontWeight: '500',
  timePillColor: 'var(--color-text-link)',
  timePillBackground: 'var(--color-bg-elevated-solid)',
  timePillBorderRadius: '100px',
  timePillPadding: '4px 12px',

  editFontSize: '14px',
  editFontWeight: '500',
  editColor: 'var(--color-text-primary)',
  editBackground: 'var(--color-bg-elevated-solid)',
  editBorderRadius: '100px',
  editPadding: '6px 14px',
}

/* ───────────────────────────────────────────────────────────────
 *  Smart time formatter — "12-1PM", "1AM-2PM", "1:30-2:40PM"
 * ─────────────────────────────────────────────────────────────── */
function timeParts(date) {
  const h = date.getHours()
  const m = date.getMinutes()
  const period = h >= 12 ? 'PM' : 'AM'
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h
  return { hour: displayHour, minute: m, period }
}

function formatTimeStr({ hour, minute }) {
  return minute === 0 ? `${hour}` : `${hour}:${String(minute).padStart(2, '0')}`
}

function formatTimeRange(startDate, endDate) {
  const s = timeParts(startDate)
  if (!endDate) return `${formatTimeStr(s)}${s.period}`
  const e = timeParts(endDate)
  const sStr = formatTimeStr(s)
  const eStr = formatTimeStr(e)
  if (s.period === e.period) return `${sStr}-${eStr}${e.period}`
  return `${sStr}${s.period}-${eStr}${e.period}`
}

function LogContent({
  log, session, profilePhotoUrl,
  onVotePoll, onEditEvent, onEditPoll, onEditCode,
  onImageClick, onGalleryClick, onVideoClick, onDocOpen,
  expandedLogs, setExpandedLogs
}) {

  /* ── EVENT branch (unchanged) ── */
  if (log.log_type === 'event') {
    const startDate = new Date(log.event_start)
    const endDate = log.event_end ? new Date(log.event_end) : null
    const dayName = startDate.toLocaleDateString('en-US', { weekday: 'long' })
    const timeRange = formatTimeRange(startDate, endDate)
    const timeDisplay = `${dayName} ${timeRange}`
    const titleBottom = log.event_description ? EVENT_CONFIG.titleMarginBottom : EVENT_CONFIG.titleMarginBottomNoDesc

    return (
      <div style={{ background: EVENT_CONFIG.cardBackground, borderRadius: EVENT_CONFIG.cardBorderRadius, padding: EVENT_CONFIG.cardPadding, marginTop: EVENT_CONFIG.cardMarginTop, maxWidth: EVENT_CONFIG.cardMaxWidth, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: EVENT_CONFIG.cardGap }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 style={{ fontSize: EVENT_CONFIG.titleFontSize, fontWeight: EVENT_CONFIG.titleFontWeight, margin: `0 0 ${titleBottom} 0`, color: EVENT_CONFIG.titleColor, fontFamily: 'var(--font-system)' }}>{log.event_title}</h3>
          {log.event_description && (
            <p style={{ fontSize: EVENT_CONFIG.descFontSize, color: EVENT_CONFIG.descColor, margin: `0 0 ${EVENT_CONFIG.descMarginBottom} 0` }}>{log.event_description}</p>
          )}
          <span style={{ fontSize: EVENT_CONFIG.timePillFontSize, color: EVENT_CONFIG.timePillColor, background: EVENT_CONFIG.timePillBackground, borderRadius: EVENT_CONFIG.timePillBorderRadius, padding: EVENT_CONFIG.timePillPadding, fontWeight: EVENT_CONFIG.timePillFontWeight, display: 'inline-block' }}>{timeDisplay}</span>
        </div>
        <button onClick={() => onEditEvent(log)} style={{ padding: EVENT_CONFIG.editPadding, background: EVENT_CONFIG.editBackground, border: 'none', borderRadius: EVENT_CONFIG.editBorderRadius, color: EVENT_CONFIG.editColor, fontSize: EVENT_CONFIG.editFontSize, cursor: 'pointer', fontWeight: EVENT_CONFIG.editFontWeight, fontFamily: 'var(--font-system)', flexShrink: 0 }}>Edit</button>
      </div>
    )
  }

  /* ── CODE branch (NEW) ──
   *
   * Renders the CodeLogCard. Tapping the card opens the folder sheet
   * for editing. The "primary language" of the folder (most used
   * across files) determines the colored strip's color, matching the
   * GitHub palette toned to Apple Calendar dark mode. */
  if (log.log_type === 'code') {
    return (
      <CodeLogCard
        log={log}
        onOpen={() => onEditCode && onEditCode(log)}
      />
    )
  }

  /* ── POLL branch — REMOVED.
   * Old poll logs in the database (`log_type === 'poll'`) will now
   * fall through to the default text branch and render their
   * `content` (likely empty), so they appear as blank logs. That's
   * the safest path until you decide whether to drop the columns.
   * If you want to hide them entirely instead, you could early-return
   * null here for log_type === 'poll'. Leaving them visible so you
   * can spot them in the feed and delete manually. */

  /* ── TEXT / MEDIA / LINK branch (existing logic, unchanged) ── */
  const isExp = expandedLogs.has(log.id)
  const isLong = (log.content && log.content.split('\n').length > 3) || (log.content && log.content.length > 180)
  const urlMatch = log.content?.match(/https?:\/\/[^\s]+/)
  const detectedUrl = urlMatch?.[0]
  const displayContent = detectedUrl && !log.media_url ? log.content.replace(detectedUrl, '').trim() : log.content

  const isDocAttachment = log.media_url && /\.(pdf|docx?|xlsx?|pptx?|txt|csv|zip|rar)$/i.test(log.media_url)
  const shouldShowContent = displayContent && !isDocAttachment

  return (
    <>
      {shouldShowContent && (
        <div>
          <p style={{ fontSize: 'var(--text-lg)', lineHeight: 'var(--leading-normal)', marginBottom: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: isExp ? 'unset' : 3, WebkitBoxOrient: 'vertical', color: 'var(--color-text-primary)' }}>{displayContent}</p>
          {isLong && (
            <button onClick={e => { e.stopPropagation(); setExpandedLogs(prev => { const n = new Set(prev); n.has(log.id) ? n.delete(log.id) : n.add(log.id); return n }) }}
              style={{ background: 'none', border: 'none', padding: '2px 0 6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--color-text-link)', fontSize: '14px', fontWeight: 'var(--weight-medium)' }}>
              {isExp ? <>Show less <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/></svg></> : <>more <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/></svg></>}
            </button>
          )}
        </div>
      )}
      {detectedUrl && !log.media_url && <LinkPreviewCard url={detectedUrl} />}
      <MediaRenderer log={log} onImageClick={onImageClick} onGalleryClick={onGalleryClick} onVideoClick={onVideoClick} onDocOpen={onDocOpen} />
    </>
  )
}

/* PERF: React.memo unchanged from before. Code logs benefit since
 * CodeLogCard itself is memoized — re-render of LogContent will not
 * re-run the syntax highlight if the log ref hasn't changed. */
export default React.memo(LogContent)